import { Link, Outlet, useRouterState, useNavigate } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import {
  LayoutGrid,
  Plus,
  ListChecks,
  KanbanSquare,
  Table2,
  LogOut,
  Sparkles,
  Settings as Cog,
  Users,
  HelpCircle,
  Calendar,
  ShoppingBag,
  ShoppingCart,
  Package,
  Megaphone,
  Eye,
  X,
  MoreHorizontal,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useCustomerView } from "@/hooks/use-customer-view";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useBrand } from "@/hooks/use-brand";
import { UserMenu } from "@/components/layout/UserMenu";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { CartDrawer, CartButton } from "@/components/CartDrawer";
import { AnnouncementDisplay } from "@/components/AnnouncementDisplay";
import { MobileMoreSheet } from "@/components/layout/MobileMoreSheet";
import { useState } from "react";

const customerNav = [
  { to: "/app/customer/dashboard", label: "Dashboard", icon: LayoutGrid },
  {
    to: "/app/customer/shop",
    label: "Shop",
    icon: ShoppingBag,
    activeMatches: ["/app/customer/shop"],
  },
  {
    to: "/app/customer/cart",
    label: "Cart",
    icon: ShoppingCart,
    activeMatches: ["/app/customer/cart", "/app/customer/checkout"],
  },
  { to: "/app/customer/jobs", label: "Jobs", icon: ListChecks },
];

const staffNav = [
  { to: "/app/staff/today", label: "Today", icon: Calendar },
  {
    to: "/app/staff/jobs-board",
    label: "Jobs",
    icon: KanbanSquare,
    activeMatches: ["/app/staff/jobs-board", "/app/staff/jobs-table", "/app/staff/jobs/"],
  },
  {
    to: "/app/staff/products",
    label: "Products",
    icon: Package,
    activeMatches: ["/app/staff/products"],
  },
  { to: "/app/staff/announcements", label: "Announcements", icon: Megaphone },
  { to: "/app/staff/users", label: "Users", icon: Users },
];

const accountNav = [
  { to: "/app/settings", label: "Settings", icon: Cog },
  { to: "/app/help", label: "Help", icon: HelpCircle },
];

const customerMobileNav = [
  { to: "/app/customer/dashboard", label: "Dashboard", icon: LayoutGrid },
  {
    to: "/app/customer/shop",
    label: "Shop",
    icon: ShoppingBag,
    activeMatches: ["/app/customer/shop"],
  },
  { to: "/app/customer/jobs", label: "Jobs", icon: ListChecks },
];

const staffMobileNav = [
  { to: "/app/staff/today", label: "Today", icon: Calendar },
  {
    to: "/app/staff/jobs-board",
    label: "Jobs",
    icon: KanbanSquare,
    activeMatches: ["/app/staff/jobs-board", "/app/staff/jobs-table", "/app/staff/jobs/"],
  },
  {
    to: "/app/staff/products",
    label: "Products",
    icon: Package,
    activeMatches: ["/app/staff/products"],
  },
  {
    to: "/app/customer/shop",
    label: "Shop",
    icon: ShoppingBag,
    activeMatches: ["/app/customer/shop"],
  },
];

export function AppShell({ children }: { children?: ReactNode }) {
  const { profile, signOut } = useAuth();
  const isStaff = profile?.role === "staff" || profile?.role === "admin";
  const isAdmin = profile?.role === "admin";

  const activeBrand = useBrand();

  // Inject brand colours as CSS custom properties when white-label is active
  useEffect(() => {
    if (profile?.white_label_enabled && profile.white_label_primary_color) {
      document.documentElement.style.setProperty(
        "--brand-primary",
        profile.white_label_primary_color,
      );
    } else {
      document.documentElement.style.removeProperty("--brand-primary");
    }
    if (profile?.white_label_enabled && profile.white_label_accent_color) {
      document.documentElement.style.setProperty(
        "--brand-accent",
        profile.white_label_accent_color,
      );
    } else {
      document.documentElement.style.removeProperty("--brand-accent");
    }
  }, [
    profile?.white_label_enabled,
    profile?.white_label_primary_color,
    profile?.white_label_accent_color,
  ]);

  const { demoMode, setDemoMode } = useDemoMode();
  const { active: viewingAsCustomer, setActive: setCustomerView } = useCustomerView();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [moreOpen, setMoreOpen] = useState(false);

  const enterCustomerView = () => {
    setCustomerView(true);
    navigate({ to: "/app/customer/shop" });
  };
  const exitCustomerView = () => {
    setCustomerView(false);
    navigate({ to: "/app/staff/today" });
  };
  const inCustomerArea = path.startsWith("/app/customer");
  const showCustomerBanner = isStaff && (viewingAsCustomer || inCustomerArea);

  // Auto-clear customer view when staff navigates back to a staff route
  useEffect(() => {
    if (isStaff && viewingAsCustomer && !inCustomerArea) {
      setCustomerView(false);
    }
  }, [isStaff, viewingAsCustomer, inCustomerArea]);

  // Global Esc shortcut to exit customer view (ignored when typing in inputs)
  useEffect(() => {
    if (!showCustomerBanner || !viewingAsCustomer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      exitCustomerView();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCustomerBanner, viewingAsCustomer]);

  return (
    <div className="flex min-h-screen w-full bg-background">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col overflow-y-auto border-r border-border/60 bg-sidebar p-4 md:flex">
        <Link to="/" className="mb-6 flex items-center gap-3 px-2">
          <img src={activeBrand.logoSrc} alt={`${activeBrand.name} logo`} className="h-9 w-auto" />
          <div className="leading-tight">
            <div className="font-bold text-foreground">{activeBrand.name}</div>
            <div className="text-xs text-muted-foreground">{activeBrand.tagline}</div>
          </div>
        </Link>

        <nav className="space-y-6">
          <NavSection title="Customer Portal" items={customerNav} path={path} />
          {isStaff && <NavSection title="Staff Hub" items={staffNav} path={path} />}
          <NavSection title="Account" items={accountNav} path={path} />
        </nav>

        {isStaff && (
          <div className="mt-4">
            <button
              type="button"
              onClick={viewingAsCustomer ? exitCustomerView : enterCustomerView}
              className="flex w-full items-center gap-2 rounded-2xl border border-dashed border-border px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-sidebar-accent hover:text-foreground"
              aria-pressed={viewingAsCustomer}
            >
              <Eye className="h-4 w-4" />
              {viewingAsCustomer ? "Exit customer view" : "View as customer"}
            </button>
          </div>
        )}

        {isStaff && (
          <div className="mt-auto space-y-3 rounded-3xl bg-gradient-soft p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkles className="h-4 w-4 text-accent" /> Demo Mode
              </div>
              <Switch checked={demoMode} onCheckedChange={setDemoMode} />
            </div>
            <p className="text-xs text-muted-foreground">
              {demoMode
                ? "Showing sample data — changes won't be saved."
                : "Showing your live workspace."}
            </p>
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {showCustomerBanner && (
          <div
            role="status"
            aria-live="polite"
            aria-label="Staff is viewing the customer portal"
            className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs sm:text-sm"
          >
            <div className="flex items-center gap-2 font-medium text-amber-700 dark:text-amber-300">
              <Eye className="h-4 w-4" aria-hidden="true" />
              <span>Viewing as customer</span>
              <span className="sr-only"> — press Escape to exit</span>
            </div>
            <button
              type="button"
              onClick={exitCustomerView}
              onKeyDown={(e) => {
                if (e.key === "Escape") exitCustomerView();
              }}
              className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 px-3 py-1 font-medium text-amber-700 hover:bg-amber-500/20 dark:text-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="Exit customer view"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" /> Exit
            </button>
          </div>
        )}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border/50 bg-background/80 px-6 py-3 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="text-sm text-muted-foreground">
              {isStaff ? "Staff Hub" : "Customer Portal"}
            </div>
            {isStaff && demoMode && (
              <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-xs font-semibold text-amber-600 border border-amber-500/20">
                Demo mode
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isStaff && <CartButton />}
            <NotificationBell />
            <div className="hidden md:block">
              <UserMenu />
            </div>
          </div>
        </header>
        <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8 pb-24 md:pb-8">
          {children ?? <Outlet />}
        </main>

        {/* Mobile bottom navigation */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <nav className="flex items-center justify-around px-2 py-2 safe-area-inset-bottom">
            {(isStaff && !viewingAsCustomer && !inCustomerArea
              ? staffMobileNav
              : customerMobileNav
            ).map((item) => {
              const active = (item as any).activeMatches
                ? (item as any).activeMatches.some((m: string) => path.startsWith(m))
                : path.startsWith(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-lg px-3 py-2 text-xs font-medium transition-colors",
                    active ? "text-primary" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon className={cn("h-5 w-5", active && "text-primary")} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className={cn(
                "flex flex-col items-center gap-1 rounded-lg px-3 py-2 text-xs font-medium transition-colors",
                moreOpen ? "text-primary" : "text-muted-foreground hover:text-foreground",
              )}
              aria-label="More options"
            >
              <MoreHorizontal className="h-5 w-5" />
              <span>More</span>
            </button>
          </nav>
        </div>
      </div>

      <CartDrawer />
      <AnnouncementDisplay
        audience={isStaff && !viewingAsCustomer && !inCustomerArea ? "staff" : "customer"}
      />
      <MobileMoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        isStaff={isStaff}
        isAdmin={isAdmin}
        onSignOut={signOut}
      />
    </div>
  );
}

function NavSection({
  title,
  items,
  path,
}: {
  title: string;
  items: { to: string; label: string; icon: typeof ListChecks; activeMatches?: string[] }[];
  path: string;
}) {
  return (
    <div>
      <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <ul className="space-y-1">
        {items.map((it) => {
          const active = it.activeMatches
            ? it.activeMatches.some((m) => path.startsWith(m))
            : path.startsWith(it.to);
          const Icon = it.icon;
          return (
            <li key={it.to}>
              <Link
                to={it.to}
                className={cn(
                  "flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-all",
                  active
                    ? "bg-gradient-hero text-primary-foreground shadow-soft"
                    : "text-foreground/80 hover:bg-sidebar-accent hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
                {it.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
